import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './Navigation.module.css';
import logo from '../../assets/images/logo.png';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);

  const navItems = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'About' },
    { id: 'solutions', label: 'Solutions' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'location', label: 'Location' },
    { id: 'contact', label: 'Contact' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      
      const sections = document.querySelectorAll('[data-section]');
      const scrollPosition = window.scrollY + 100;

      sections.forEach((section) => {
        const sectionTop = section.offsetTop;
        const sectionHeight = section.offsetHeight;
        const sectionId = section.getAttribute('data-section');

        if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
          setActiveSection(sectionId);
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.querySelector(`[data-section="${sectionId}"]`);
    if (element) {
      const offset = 100;
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
      setIsMobileMenuOpen(false);
    }
  };

  const handleGetStarted = () => {
    window.open('https://credence.helahub.co', '_blank', 'noopener,noreferrer');
  };

  return (
    <motion.nav 
      className={`${styles.navigation} ${isScrolled ? styles.scrolled : ''}`}
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
    >
      <div className={styles.navWrapper}>
        <div className={styles.container}>
          
          {/* Logo */}
          <motion.div 
            className={styles.logoContainer}
            whileHover={{ scale: 1.03 }}
            transition={{ duration: 0.3, ease: [0.19, 1, 0.22, 1] }}
            onClick={() => scrollToSection('home')}
          >
            <div className={styles.logoIconWrapper}>
              <img src={logo} alt="Scorex Logo" className={styles.logoIcon} />
            </div>
           
          </motion.div>

          {/* Desktop Navigation */}
          <div className={styles.navCenter}>
            <ul className={styles.navList}>
              {navItems.map((item, index) => (
                <motion.li 
                  key={item.id}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.08, duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
                  onMouseEnter={() => setHoveredItem(item.id)}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  <button
                    className={`${styles.navLink} ${activeSection === item.id ? styles.active : ''}`}
                    onClick={() => scrollToSection(item.id)}
                  >
                    <span className={styles.navLinkText}>{item.label}</span>
                    
                    {hoveredItem === item.id && (
                      <motion.div 
                        className={styles.hoverUnderline}
                        layoutId="hoverUnderline"
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        exit={{ scaleX: 0 }}
                        transition={{ duration: 0.4, ease: [0.19, 1, 0.22, 1] }}
                      />
                    )}
                    
                    {activeSection === item.id && (
                      <motion.div 
                        className={styles.activeUnderline}
                        layoutId="activeUnderline"
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
                      />
                    )}
                  </button>
                </motion.li>
              ))}
            </ul>
          </div>

          {/* Desktop CTA Buttons */}
          <div className={styles.ctaContainer}>
            <motion.button 
              className={styles.contactSalesButton}
              whileHover={{ scale: 1.03, y: -2 }}
              whileTap={{ scale: 0.97 }}
              transition={{ duration: 0.3, ease: [0.19, 1, 0.22, 1] }}
              onClick={() => scrollToSection('contact')}
            >
              Contact Sales
            </motion.button>
            
            <motion.button 
              className={styles.getStartedButton}
              whileHover={{ scale: 1.03, y: -2 }}
              whileTap={{ scale: 0.97 }}
              transition={{ duration: 0.3, ease: [0.19, 1, 0.22, 1] }}
              onClick={handleGetStarted}
            >
              Get Started
              <div className={styles.buttonGlow}></div>
            </motion.button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className={styles.mobileMenuButton}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span className={`${styles.hamburger} ${isMobileMenuOpen ? styles.open : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div 
            className={styles.mobileMenu}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4, ease: [0.19, 1, 0.22, 1] }}
          >
            <ul className={styles.mobileNavList}>
              {navItems.map((item, index) => (
                <motion.li 
                  key={item.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                >
                  <button
                    className={`${styles.mobileNavLink} ${activeSection === item.id ? styles.active : ''}`}
                    onClick={() => scrollToSection(item.id)}
                  >
                    <span>{item.label}</span>
                    {activeSection === item.id && (
                      <div className={styles.mobileActiveIndicator} />
                    )}
                  </button>
                </motion.li>
              ))}
            </ul>
            
            <div className={styles.mobileCta}>
              <button 
                className={styles.mobileContactSales}
                onClick={() => scrollToSection('contact')}
              >
                Contact Sales
              </button>
              <button 
                className={styles.mobileGetStarted}
                onClick={handleGetStarted}
              >
                Get Started
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navigation;